import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router";
import { BrainScreen } from "@/features/brain/brain-screen";
import { DecisionsScreen } from "@/features/brain/decisions-screen";
import { BrainEntryScreen } from "@/features/brain/brain-entry-screen";
import { DocumentViewerScreen } from "@/features/brain/document-viewer-screen";
import { AskStreamScope } from "@/features/chat/ask-stream";
import { ChatScreen } from "@/features/chat/chat-screen";
import { HistoryScreen } from "@/features/chat/history-screen";
import { CrewScreen } from "@/features/crew/crew-screen";
import { JournalScreen } from "@/features/journal/journal-screen";
import { JournalEntryScreen } from "@/features/journal/journal-entry-screen";
import { JournalEntryForm } from "@/features/journal/journal-entry-form";
import { ProjectPicker } from "@/features/picker/project-picker";
import { ProjectScreen } from "@/features/project/project-screen";
import { NewProjectScreen } from "@/features/project/setup/new-project-screen";
import { SetupWizard } from "@/features/project/setup/wizard";
import { SetupEntry } from "@/features/project/setup/setup-entry";
import { SetupDoneScreen } from "@/features/project/setup/setup-done";
import { GoogleDriveCallback } from "@/features/project/google-drive-callback";
import { MicrosoftCallback } from "@/features/project/microsoft-callback";
import { TodayScreen } from "@/features/today/today-screen";
import { ImportReviewScreen } from "@/features/review/import-review-screen";
import { ProjectReviewScreen } from "@/features/review/project-review-screen";
import { PickerShell } from "./app-shell";
import { ProjectLayout } from "./project-layout";
import { useProjectContext } from "./project-context";

// The style guide is a development tool, not part of the product. Loading it
// lazily behind the DEV flag keeps it out of the production bundle entirely.
const StyleguideScreen = lazy(async () => {
  const m = await import("@/features/styleguide/styleguide-screen");
  return { default: m.StyleguideScreen };
});

/**
 * Two levels of chrome.
 *
 * `PickerShell` is above any project: the picker itself and the create flow.
 * `ProjectLayout` owns `/projects/:projectId`, resolves that id once, and
 * renders `AppShell` with the full nav for everything under it.
 *
 * `/` is the picker, not a redirect to the last project used. The journal is
 * *written*, not only read, so landing implicitly in the wrong site would file
 * a day's record against it — see
 * `project_intelligence/data-model/0001-tenant-has-many-projects.md`.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Outside any shell: this renders inside the OAuth popup, where a
          sidebar and topbar would be nonsense. */}
      <Route
        path="connectors/google-drive/callback"
        element={<GoogleDriveCallback />}
      />
      {/* "Confirm with Microsoft" comes back here, in its popup
          (`plans/outlook-sync.md`). */}
      <Route path="connectors/microsoft/callback" element={<MicrosoftCallback />} />

      <Route element={<PickerShell />}>
        <Route index element={<ProjectPicker />} />
        {/* Step 1 has no project yet, so it cannot live under
            `/projects/:projectId`. It creates one and then navigates into it. */}
        <Route path="projects/new" element={<NewProjectScreen />} />
        {import.meta.env.DEV ? (
          <Route
            path="styleguide"
            element={
              <Suspense fallback={null}>
                <StyleguideScreen />
              </Suspense>
            }
          />
        ) : null}
      </Route>

      <Route path="projects/:projectId" element={<ProjectLayout />}>
        <Route index element={<TodayScreen />} />
        <Route path="crew" element={<CrewScreen />} />
        <Route path="journal" element={<JournalScreen />} />
        {/* `journal/new` is gone: filing a note is a modal composer that opens
            over whatever screen invoked it, not a screen of its own (JRNL-07).
            `journal/:entryId/edit` stays — amending a day's *record* is a
            different act from filing a note, and the composer does not do it. */}
        <Route path="journal/:entryId" element={<JournalEntryScreen />} />
        <Route path="journal/:entryId/edit" element={<JournalEntryFormRoute />} />
        {/* The answer being written is held here, around every Brain route,
            not by the Ask screen (2026-09-27). The History tab is a route of
            its own, and leaving Ask for it used to throw the stream away: the
            question opened from History did not say another was still being
            answered, and a second ask did not stop the first. See
            `ask-stream.tsx`. */}
        <Route element={<AskStreamScope />}>
          {/* `/brain` has no content of its own — it is five tabs over one
              corpus, and Ask is the first of them. Redirecting rather than
              picking a default screen keeps every tab a real, linkable address
              instead of one of them being "the Brain" and the rest being
              somewhere you get to. */}
          <Route path="brain" element={<Navigate to="ask" replace />} />
          <Route path="brain/sources" element={<BrainScreen />} />
          <Route path="brain/decisions" element={<DecisionsScreen />} />
          {/* What has been asked, on a tab of its own (2026-09-27,
              `product/0013`). Each row opens its question in Ask, below. */}
          <Route path="brain/history" element={<HistoryScreen />} />
          {/* Every one of these is static-first, so they rank above
              `brain/:entryId`. React Router ranks them that way regardless, but
              the reading order should match the matching order.

              This used to be load-bearing: with text ids, `/brain/ask` could
              genuinely name a knowledge entry called "ask". Ids are uuids now
              (`project_intelligence/data-model/0006-ids-are-uuids.md`) so no
              entry id can ever collide with a literal segment — the ordering is
              back to being merely correct rather than a defence. */}
          {/* `ask`, not `chat`, and one screen for both. The Brain answers one
              question at a time, so there is no conversation to resolve to and
              no entry route redirecting into the most recent one — a bare
              `brain/ask` is the ask box with nothing asked yet. See
              `product/0005-a-question-is-answered-once.md`. */}
          <Route path="brain/ask" element={<ChatScreen />} />
          <Route path="brain/ask/:questionId" element={<ChatScreen />} />
          <Route path="brain/files/:docId" element={<DocumentViewerScreen />} />
          <Route path="brain/:entryId" element={<BrainEntryScreen />} />
        </Route>
        <Route path="overview" element={<ProjectScreen />} />
        {/* The review after an import (Phase 3f): a screen of its own, reached
            by the banner on the Brain and on setup's review step. Not under
            `brain/`, because an import is not one of the Brain's five faces. */}
        <Route path="imports/:importId" element={<ImportReviewScreen />} />
        {/* Every open question of the project, from the one banner (2026-10-03). */}
        <Route path="review" element={<ProjectReviewScreen />} />
        {/* `setup` with no step resolves to the saved cursor, so the sidebar
            and any bookmark can link to it without knowing where the user
            got to. */}
        <Route path="setup" element={<SetupEntry />} />
        {/* Where setup ends: "Project successfully set up", and the way to
            Sources. Static, so it ranks above `setup/:step`. */}
        <Route path="setup/done" element={<SetupDoneScreen />} />
        <Route path="setup/:step" element={<SetupWizard />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** The journal form needs the project id, which only the context carries. */
function JournalEntryFormRoute() {
  const { projectId } = useProjectContext();
  return <JournalEntryForm projectId={projectId} />;
}
